<template>
  <li class="work-order">
    <div class="work-order__group">
      <div class="typo-title">
        {{ member }}
      </div>
      <template v-if="job.status === 'provisionally_scheduled'">
        <div
          v-for="(rating, detMech) in workOrder.activity.member.deterioration_mechanism_ratings"
          :key="detMech"
          :class="[
            'det-mech',
            `det-mech--${rating}`,
            {
              hover: hoverDetMechs.includes(detMech),
              selected: selectedDetMechs.includes(detMech)
            }
          ]"
        >
          <div class="typo-smallish">
            {{ $t(`deterioration_mechanisms.${detMech}.name`) }}
          </div>
          <div class="rating" />
          <div class="typo-rating">
            {{ rating }}
          </div>
        </div>
      </template>
      <template v-else-if="job.status === 'transferred_to_planner'">
        <template v-if="job.policy.update">
          <div class="input external-reference">
            <input
              v-model="workOrder.external_reference"
              :placeholder="$t('shafts.jobs.work_order_number')"
            >
          </div>
          <div class="checkbox checkbox--rounded checkbox--incomplete">
            <input
              :id="`completed_${workOrder.id}`"
              v-model="workOrder.completed"
              class="checkbox__input"
              type="checkbox"
            >
            <label
              class="checkbox__label"
              :for="`completed_${workOrder.id}`"
            >
              {{ $t('shafts.jobs.this_work_order_has_been_completed') }}
            </label>
          </div>
        </template>
        <template v-else>
          <span class="external-reference">{{ workOrder.external_reference }}</span>
          <div
            v-if="workOrder.completed"
            class="read-only-checkbox"
          >
            <UseSvg
              class="icon"
              name="checkmark"
            />
            <strong>{{ $t('shafts.jobs.this_work_order_has_been_completed') }}</strong>
          </div>
        </template>
      </template>
      <template v-else>
        <span>{{ workOrder.external_reference }}</span>
      </template>
    </div>
    <div class="work-order__actions">
      <Dropdown
        v-if="workOrder.policy.destroy"
        :items="[{ label: $t('general.delete'), value: 'delete' }]"
        @click-item="onClickDropdownItem"
      >
        <UseSvg
          class="icon"
          name="vertical_ellipsis"
        />
      </Dropdown>
    </div>
  </li>
  <Dialog
    v-if="dialogIsOpen"
    :title="$t('shafts.jobs.work_order_delete_confirmation_title')"
    @dismissDialog="toggleDialog"
  >
    <template #actions>
      <button
        aria-label="delete-work-order"
        class="button button--inverse"
        type="button"
        @click="deleteWorkOrder"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import I18n from 'i18n-js';
import { useRoute } from 'vue-router';

import useAjax from '@/functions/use_ajax';

import UseSvg from '@/components/generic/use_svg.vue';
import Dropdown from '@/components/generic/dropdown.vue';
import Dialog from '@/components/generic/dialog.vue';

const props = defineProps({
  job: { type: Object, required: true },
  workOrder: { type: Object, required: true },
  hoverDetMechs: { type: Array, default: () => [] },
  selectedDetMechs: { type: Array, default: () => [] },
});

const emit = defineEmits(['updateJob']);

const route = useRoute();
const ajax = useAjax();
const dialogIsOpen = ref(false);
const confirmDelete = ref(false);

const member = computed(() => {
  let location = props.workOrder.activity.member.location;
  if (location.shaft_element) return `${I18n.t('shafts.issues.set')} #${location.set_number}`;
  return location.station_template_member.name;
});

function onClickDropdownItem({ value }) {
  switch (value) {
    case 'delete':
      confirmDeleteAction();
      break;
  }
}

function toggleDialog() {
  dialogIsOpen.value = !dialogIsOpen.value;
}

function confirmDeleteAction() {
  confirmDelete.value = true;
  toggleDialog();
}

async function deleteWorkOrder() {

  const { organisationId, shaftId, jobId } = route.params;

  const { data, ok } = await ajax(`/organisations/${organisationId}/shafts/${shaftId}/jobs/${jobId}`, {
    method: 'PATCH',
    body: { job: { work_orders_attributes: [{ id: props.workOrder.id, _destroy: true }] } }
  });

  if (ok) {
    emit('updateJob', data);
    toggleDialog();
  }
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

li {
  border-radius: map-get($border-radius, mega);
  background-color: map-get($background-color, lighter);
  padding: bu(3) bu(3) bu(1.5);
  margin-bottom: bu(2);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.work-order {
  $block: &;

  #{$block}__group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;

    & > * {
      margin-bottom: bu(1.5);
    }
  }

  #{$block}__actions {
    margin-bottom: bu(1.5);
  }
}

.typo-title {
  margin-right: bu(5);
  padding: bu(0.5) bu(1);
}

.typo-smallish {
  margin-right: bu(1.5);
}

.rating {
  width: bu(1.5);
  height: bu(1.5);
  position: relative;
  display: inline-block;
  border-radius: map-get($border-radius, circle);
  margin-right: bu(0.75);

  @each $rating, $color in $condition-rating-color {
    .det-mech--#{$rating} & {
      background-color: map-get($color, default);
    }
  }
}

.typo-rating {
  font-weight: 700;
  width: bu(2.5);
  text-align: center;
}

.det-mech {
  margin-right: bu(2.5);
  display: flex;
  align-items: center;
  border: 1px solid transparent;
  border-radius: map-get($border-radius, heavy);
  padding: bu(0.5) bu(1);

  &.selected, &.hover {
    background-color: map-get($background-color, dark);
  }

  &.hover {
    @each $rating, $color in $condition-rating-color {
      &.det-mech--#{$rating} {
        border-color: map-get($color, default);
      }
    }
  }
}

.external-reference {
  flex-grow: 1;
  max-width: bu(40);
  margin: bu(0.5) bu(4) bu(2) bu(1);
}

.checkbox {
  $block: &;
  margin: bu(0.5) bu(4) bu(2) bu(1);

  #{$block}__input:checked + .checkbox__label {
    font-weight: 700;
  }
}

.read-only-checkbox {
  display: flex;
  align-items: center;

  svg {
    margin-right: bu(2);
  }
}

.dropdown {
  margin-left: auto;

  @include breakpoint(medium) {
    visibility: hidden;

    li:hover & {
      visibility: visible;
    }
  }
}
</style>
